import React from 'react';
import clsx from 'clsx';
import { Container, Typography, ButtonVanilla, ArrowLeftIcon, ArrowRightIcon } from '@ag1/ui';
import { vars } from '@ag1/ui/theme';
import { SanityKeyed, ParagraphTextPage as ParagraphTextPageProps } from 'types/studio/studio';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { getSanityLinkUrl } from '@portal/shared/utils/getSanityLinkUrl';
import { useLocale } from 'shared/hooks/useLocale';
import { Paragraph } from './components/Paragraph/Paragraph';
import styles from './ParagraphTextPage.module.scss';
import { CommonSectionProps } from '../types';

export const ParagraphTextPage = ({
  _key: key,
  wideContainer,
  divider,
  pageHeadline,
  paragraphsArray,
  footerText,
  footerTextAlignment = 'left',
  twoColumns,
  buttonText,
  buttonUrl,
  buttonVariant,
  buttonShape,
  showButtonIcon,
  buttonIconPosition,
  padding,
  backgroundColor: backgroundColorProp,
  textColor: textColorProp
}: SanityKeyed<ParagraphTextPageProps> & CommonSectionProps) => {
  const locale = useLocale();

  const backgroundColor =
    backgroundColorProp && backgroundColorProp in vars.colors.background
      ? vars.colors.background[backgroundColorProp as keyof typeof vars.colors.background]
      : vars.colors.background.white;

  const textColor =
    textColorProp && textColorProp in vars.colors.text
      ? vars.colors.text[textColorProp as keyof typeof vars.colors.text]
      : vars.colors.text.primary;

  let icon = null;
  if (showButtonIcon) {
    if (buttonIconPosition === 'left') {
      icon = <ArrowLeftIcon size="24" />;
    } else if (buttonIconPosition === 'right') {
      icon = <ArrowRightIcon size="24" />;
    }
  }

  const buttonHref = buttonUrl ? getSanityLinkUrl(buttonUrl, locale) : undefined;

  return (
    <div style={{ backgroundColor }}>
      {divider && <div className={styles.divider} />}
      <Container
        id={key}
        className={clsx(getSectionPaddingClasses(styles, padding), {
          [styles.defaultPadding]: wideContainer
        })}>
        <div className={clsx(wideContainer ? styles.wideContainer : styles.container)}>
          {pageHeadline && (
            <Typography
              className={styles.headline}
              variant="headlineSans"
              size="2"
              component="div"
              noPadding
              style={{ color: textColor }}>
              <CustomPortableText value={pageHeadline} />
            </Typography>
          )}
          <div className={clsx(styles.content, twoColumns && styles.twoColumns)}>
            {paragraphsArray &&
              paragraphsArray.map((termItem) => (
                <Paragraph
                  wideContainer={wideContainer}
                  headline={termItem.headline}
                  text={termItem.text}
                  key={termItem._key}
                  isMono={termItem.isMono}
                  textColor={textColor}
                />
              ))}
          </div>
          {(buttonText || footerText) && (
            <div className={styles.bottomSection}>
              {buttonText && (
                <ButtonVanilla
                  className={styles.button}
                  href={buttonHref}
                  variant={buttonVariant}
                  size="L"
                  shape={buttonShape || 'round'}
                  iconPosition={buttonIconPosition}
                  icon={icon}>
                  {buttonText}
                </ButtonVanilla>
              )}
              {footerText && (
                <Typography
                  className={clsx(styles.footerText, {
                    [styles.footerTextInverse]: textColorProp === 'primaryInverse',
                    [styles.footerTextLeft]: footerTextAlignment === 'left',
                    [styles.footerTextCenter]: footerTextAlignment === 'center',
                    [styles.footerTextRight]: footerTextAlignment === 'right'
                  })}
                  fontStyle="italic"
                  variant="body"
                  size="2"
                  noPadding>
                  {footerText}
                </Typography>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};
